import { LanguagePreferenceDisplay } from '@quromedical/fhir-common';
import type {
  PersonContactWithAddressEmergencyAccess,
  PersonGeneral,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { IdentifierCodeDisplay } from 'core/display';
import { getDisplayName } from 'core/person';
import { inputDateFormatter, type RowConfig } from 'design-system';
import { strings } from 'strings';

export const getMinimalPersonalDetailsRows = (
  general: Partial<PersonGeneral> = {},
): RowConfig[] => {
  const displayName = getDisplayName(general);

  return [
    {
      icon: 'person-outline',
      fields: [
        {
          type: 'text',
          label: strings.LabelNameAndSurname,
          display: displayName,
        },
      ],
    },
    {
      icon: 'gender-male-female',
      fields: [
        {
          type: 'text',
          label: strings.Gender,
          display: general.gender,
        },
      ],
    },
  ];
};

export const getFullPersonalDetailsRows = (
  general: Partial<PersonGeneral> = {},
): RowConfig[] => {
  const date = general.birthDate
    ? inputDateFormatter.date(new Date(general.birthDate))
    : '';

  const minimalPersonalDetails = getMinimalPersonalDetailsRows(general);
  const extendedPersonalDetails: RowConfig[] = [
    {
      icon: 'cake',
      fields: [
        {
          type: 'text',
          label: strings.DateOfBirth,
          display: date,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelIDNumber,
          display: general.identifierValue,
        },
        {
          type: 'text',
          label: strings.LabelIdentifierType,
          display:
            general.identifierCode &&
            IdentifierCodeDisplay[general.identifierCode],
        },
      ],
    },
  ];

  return [...minimalPersonalDetails, ...extendedPersonalDetails];
};

export const getContactDetailsRows = (
  contact: Partial<PersonContactWithAddressEmergencyAccess> = {},
  hasEmergencyAccess = false,
): RowConfig[] =>
  compact([
    {
      icon: 'phone-iphone',
      fields: [
        {
          type: 'text',
          label: strings.Phone,
          display: contact.telecomPhoneNumbers?.[0],
        },
        {
          type: 'text',
          label: strings.Phone2,
          display: contact.telecomPhoneNumbers?.[1],
        },
      ],
    },
    {
      icon: 'email-outline',
      fields: [
        {
          type: 'text',
          label: strings.Email,
          display: contact.telecomEmail,
        },
      ],
    },
    {
      icon: 'pin-drop',
      fields: [
        {
          type: 'list',
          label: strings.LabelAddress,
          display: contact.addressLines,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelPostalCode,
          display: contact.addressPostalCode,
        },
        {
          type: 'text',
          label: strings.LabelCity,
          display: contact.addressCity,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelCountry,
          display: contact.addressCountry,
        },
      ],
    },
    hasEmergencyAccess && {
      icon: 'error-outline',
      fields: [
        {
          type: 'text',
          label: strings.LabelInCaseOfEmergency,
          display: contact.addressEmergencyAccess,
        },
      ],
    },
  ]);

export const getContactDetailsWithoutAddressRows = (
  contact: Partial<PersonContactWithAddressEmergencyAccess> = {},
  hasEmergencyAccess = false,
): RowConfig[] =>
  compact([
    {
      icon: 'phone-iphone',
      fields: [
        {
          type: 'text',
          label: strings.Phone,
          display: contact.telecomPhoneNumbers?.[0],
        },
        {
          type: 'text',
          label: strings.Phone2,
          display: contact.telecomPhoneNumbers?.[1],
        },
      ],
    },
    {
      icon: 'email-outline',
      fields: [
        {
          type: 'text',
          label: strings.Email,
          display: contact.telecomEmail,
        },
        {
          type: 'text',
          label: strings.LanguagePreference,
          display: contact.languagePreference
            ? LanguagePreferenceDisplay[contact.languagePreference]
            : 'English',
        },
      ],
    },

    hasEmergencyAccess && {
      icon: 'error-outline',
      fields: [
        {
          type: 'text',
          label: strings.LabelInCaseOfEmergency,
          display: contact.addressEmergencyAccess,
        },
      ],
    },
  ]);

export const getAddressDetailsRows = (
  contact: Partial<PersonContactWithAddressEmergencyAccess> = {},
): RowConfig[] =>
  compact([
    {
      icon: 'pin-drop',
      fields: [
        {
          type: 'list',
          label: strings.LabelAddress,
          display: contact.addressLines,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelPostalCode,
          display: contact.addressPostalCode,
        },
        {
          type: 'text',
          label: strings.LabelCity,
          display: contact.addressCity,
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelCountry,
          display: contact.addressCountry,
        },
      ],
    },
  ]);
